// «QUIÉN NO ACUMULA» (F1, 19-oct-2026 — petición #1465 de Paco, Silvestre). Tarjeta en Mi Negocio › Cómo registro visitas (antes Programa de lealtad),
// debajo de «Modelo de Lealtad» («Define cómo acumulan todos»: las excepciones van al lado). Vacía = una línea.
// Gratis en todos los planes. Agregar/quitar: dueño y gerente (el servidor exige loyalty:manage).
// Muestra además, en solo lectura, el candado de ticket del POS (hoy solo lo ve el superadmin).
import { useCallback, useEffect, useState } from 'react';
import { Ban, Loader2, Lock, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { accrualBlocksApi, fmtHasta, type AccrualBlock, type AccrualBlocksList } from '../../../api/accrualBlocksApi';
import AccrualBlockModal from '../../clients/components/AccrualBlockModal';
import { useAuth } from '../../../context/AuthContext';

const tel4 = (p?: string | null) => (p ? `${String(p).slice(0, 3)}…${String(p).slice(-2)}` : '');
const fecha = (s: string) => {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
};

export default function AccrualBlocksCard() {
  const { currentUser } = useAuth();
  const puedeEditar = !currentUser?.baseRole || currentUser.baseRole === 'owner' || currentUser.baseRole === 'manager';
  const [data, setData] = useState<AccrualBlocksList | null>(null);
  const [error, setError] = useState(false);
  const [modal, setModal] = useState(false);
  const [quitando, setQuitando] = useState<number | null>(null);

  const cargar = useCallback(() => {
    accrualBlocksApi.list(true).then((d) => { setData(d); setError(false); }).catch(() => setError(true));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const quitar = async (b: AccrualBlock) => {
    if (!window.confirm(`¿${b.first_name || 'Este cliente'} vuelve a acumular visitas y a poder canjear?`)) return;
    setQuitando(b.contact_id);
    try { await accrualBlocksApi.remove(b.contact_id); cargar(); }
    catch { window.alert('No se pudo quitar. Intenta de nuevo.'); }
    finally { setQuitando(null); }
  };

  if (error && !data) return null;   // sin permiso / sin red: no estorba la página
  const blocks = data?.blocks || [];

  return (
    <div className="bg-surface p-5 rounded-lg border border-line shadow-sm" data-testid="tarjeta-no-acumula">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <div className="flex items-center">
          <Ban className="h-5 w-5 text-amber-500 mr-2" />
          <h3 className="text-lg font-medium text-content">Quién no acumula{blocks.length ? ` (${blocks.length})` : ''}</h3>
        </div>
        {puedeEditar && (
          <button type="button" onClick={() => setModal(true)} data-testid="no-acumula-agregar"
            className="inline-flex items-center gap-1 rounded-md border border-line px-2.5 py-1.5 text-xs font-medium text-content-2 hover:bg-surface-2">
            <Plus className="h-3.5 w-3.5" /> Agregar
          </button>
        )}
      </div>
      <p className="text-sm text-content-3">
        Empleados, influencers o cuentas de prueba que pueden usar el kiosco pero no suman visitas. Su saldo se congela: no canjean mientras estén aquí.
      </p>

      {!data ? (
        <p className="mt-3 flex items-center gap-1 text-xs text-content-3"><Loader2 className="h-3 w-3 animate-spin" /> Cargando…</p>
      ) : blocks.length === 0 ? (
        <p className="mt-3 text-sm text-content-3" data-testid="no-acumula-vacia">Nadie excluido.</p>
      ) : (
        <ul className="mt-3 divide-y divide-line rounded-lg border border-line" data-testid="no-acumula-lista">
          {blocks.map((b) => (
            <li key={b.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
              <Link to={`/dashboard/clients/${b.contact_id}/history`} className="min-w-0 flex-1 truncate font-medium text-content hover:underline">
                {[b.first_name, b.last_name].filter(Boolean).join(' ') || 'Cliente'}
              </Link>
              <span className="text-xs text-content-3">{tel4(b.phone)}</span>
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                {b.reason_label}
              </span>
              <span className="text-xs text-content-3" title={b.note || undefined}>
                desde {fecha(b.starts_at)} · {fmtHasta(b.ends_on)}{b.created_by_name ? ` · ${b.created_by_name}` : ''}
              </span>
              {puedeEditar && (
                <button type="button" onClick={() => quitar(b)} disabled={quitando === b.contact_id}
                  className="text-xs text-content-3 hover:text-red-600 hover:underline disabled:opacity-50">
                  {quitando === b.contact_id ? 'Quitando…' : 'Quitar'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {data?.ticket_lock.enabled && (
        <p className="mt-3 flex items-start gap-1.5 text-xs text-content-3" data-testid="no-acumula-candado">
          <Lock className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>
            Además tienes activo el <b>candado de ticket</b>: en el kiosco solo suma quien compró en tu caja en los últimos {data.ticket_lock.window_min} minutos.
            Para cambiarlo, escríbenos.
          </span>
        </p>
      )}

      {modal && (
        <AccrualBlockModal onClose={() => setModal(false)} onSaved={() => { setModal(false); cargar(); }} />
      )}
    </div>
  );
}
